import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { usePayStore, PayChannel } from '@/stores/payStore';
import { QRCodeCanvas } from './QRCodeCanvas';
import wechatIcon from '@/assets/wechat.png';
import alipayIcon from '@/assets/alipay.png';

export interface PayModalProps {
  /** 支付成功后的回调函数 (例如刷新用户信息或订阅状态) */
  onSuccess?: () => void;
  /** 购买协议点击跳转链接，默认 '/doc/purchase-terms.html' */
  termsUrl?: string;
}

export const PayModal: React.FC<PayModalProps> = ({
  onSuccess,
  termsUrl = '/doc/purchase-terms.html',
}) => {
  const {
    payModalVisible,
    payType,
    qrCodeLoading,
    paymentData,
    paymentRequest,
    closePayModal,
    setPayType,
    fetchPaymentQRCode,
    getPaymentStatus,
  } = usePayStore();

  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(300);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);

  const timerRef = useRef<any>(null);
  const statusCheckTimerRef = useRef<any>(null);

  // 格式化倒计时时间 mm:ss
  const countdownText = useMemo(() => {
    const m = Math.floor(timeLeft / 60);
    const s = timeLeft % 60;
    return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
  }, [timeLeft]);

  // 显示简易 Toast 提示
  const showToast = useCallback((text: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToastMsg({ type, text });
    setTimeout(() => {
      setToastMsg(null);
    }, 3000);
  }, []);

  // 清除所有定时器
  const clearAllTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (statusCheckTimerRef.current) {
      clearInterval(statusCheckTimerRef.current);
      statusCheckTimerRef.current = null;
    }
  }, []);

  // 轮询查询支付状态
  const checkStatus = useCallback(async (isManual = false) => {
    if (!paymentData.orderId) return;

    const order = await getPaymentStatus(
      paymentData.orderId,
      paymentData.orderType,
      paymentData.orderNo
    );

    const status = order?.payment_status;

    if (status === 'SUCCEEDED') {
      showToast('支付成功！', 'success');
      clearAllTimers();
      setTimeout(() => {
        closePayModal();
        if (onSuccess) onSuccess();
      }, 1000);
    } else if (isManual) {
      if (status === 'PAYING' || status === 'INITIATED') {
        showToast('未检测到支付成功，请扫码完成后重试', 'info');
      } else if (status === 'FAILED' || status === 'CLOSED') {
        showToast('订单已失效或支付失败，请重新发起', 'error');
      } else {
        showToast('暂未查询到支付结果，请稍候', 'info');
      }
    }
  }, [paymentData.orderId, paymentData.orderType, paymentData.orderNo, getPaymentStatus, showToast, clearAllTimers, closePayModal, onSuccess]);

  // 启动 300s 倒计时与 3s 状态轮询
  const startTimerAndCheck = useCallback(() => {
    clearAllTimers();
    setTimeLeft(300);
    setIsExpired(false);

    // 1. 倒计时定时器
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          clearAllTimers();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 2. 轮询状态定时器 (每 3 秒)
    statusCheckTimerRef.current = setInterval(() => {
      checkStatus(false);
    }, 3000);
  }, [clearAllTimers, checkStatus]);

  // 切换支付渠道 (WECHAT / ALIPAY)
  const handleChannelSwitch = async (channel: PayChannel) => {
    if (qrCodeLoading || !isAgreed || channel === payType) return;
    setPayType(channel);
    if (paymentRequest) {
      await fetchPaymentQRCode(paymentRequest.orderId, paymentRequest.orderType, channel);
      startTimerAndCheck();
    }
  };

  // 重新刷新二维码
  const handleRefresh = async () => {
    if (paymentRequest) {
      await fetchPaymentQRCode(paymentRequest.orderId, paymentRequest.orderType, payType);
      startTimerAndCheck();
    }
  };

  // 监听弹窗显示隐逻辑
  useEffect(() => {
    if (payModalVisible) {
      startTimerAndCheck();
    } else {
      clearAllTimers();
      setIsAgreed(false);
    }

    return () => {
      clearAllTimers();
    };
  }, [payModalVisible, startTimerAndCheck, clearAllTimers]);

  if (!payModalVisible) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      {/* 遮罩背景 */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity animate-[fadeIn_0.2s_ease-out]"
        onClick={closePayModal}
      />

      {/* 弹窗主体卡片 */}
      <div className="relative z-10 w-full max-w-md bg-white border border-foreground-100/80 rounded-[28px] shadow-[0_24px_60px_rgba(0,0,0,0.12)] overflow-hidden transition-all transform animate-[scaleIn_0.2s_ease-out]">
        {/* 关闭按钮 */}
        <button
          onClick={closePayModal}
          className="absolute top-4 right-4 z-20 text-foreground-400 hover:text-foreground-700 p-2 rounded-full hover:bg-background-100 transition-colors"
          title="关闭"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* 顶部消息 Toast 提示 */}
        {toastMsg && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full text-xs font-medium shadow-xl animate-[slideDown_0.2s_ease-out] flex items-center gap-2 bg-foreground-950 text-white border border-white/10">
            {toastMsg.type === 'success' && <span className="text-emerald-400">✓</span>}
            {toastMsg.type === 'error' && <span className="text-rose-400">✕</span>}
            {toastMsg.type === 'info' && <span className="text-sky-400">i</span>}
            <span>{toastMsg.text}</span>
          </div>
        )}

        <div className="flex flex-col items-center px-8 py-7">
          {/* 金额提示区 */}
          <div className="text-center mt-2 mb-5">
            <div className="text-foreground-400 text-xs font-medium mb-1.5">扫码完成支付</div>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-primary-600 text-2xl font-semibold">¥</span>
              <span className="text-primary-600 text-4xl font-bold font-mono tracking-tight">
                {paymentData.amount || '0.00'}
              </span>
            </div>
          </div>

          {/* 支付方式 Tab 切换 */}
          <div className="relative w-full flex flex-col items-center">
            <div className="w-full mb-5 bg-background-100 p-1.5 rounded-2xl flex relative items-center border border-foreground-100/60">
              {/* 高亮背景分段滑块 */}
              <div
                className={`absolute h-10 transition-all duration-300 ease-out rounded-xl shadow-sm ${
                  payType === 'WECHAT' ? 'bg-[#07C160]' : 'bg-[#1677FF]'
                }`}
                style={{
                  width: 'calc(50% - 6px)',
                  transform: `translateX(${payType === 'WECHAT' ? '0' : '100%'})`,
                }}
              />

              <button
                disabled={qrCodeLoading || !isAgreed}
                onClick={() => handleChannelSwitch('WECHAT')}
                className={`flex-1 relative z-10 py-2.5 flex justify-center items-center gap-2 transition-all duration-300 ${
                  payType === 'WECHAT' ? 'text-white font-semibold' : 'text-foreground-500 hover:text-foreground-800'
                } ${qrCodeLoading || !isAgreed ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <img src={wechatIcon} alt="微信支付" className="w-5 h-5 object-contain" />
                <span className="text-sm">微信支付</span>
              </button>

              <button
                disabled={qrCodeLoading || !isAgreed}
                onClick={() => handleChannelSwitch('ALIPAY')}
                className={`flex-1 relative z-10 py-2.5 flex justify-center items-center gap-2 transition-all duration-300 ${
                  payType === 'ALIPAY' ? 'text-white font-semibold' : 'text-foreground-500 hover:text-foreground-800'
                } ${qrCodeLoading || !isAgreed ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <img src={alipayIcon} alt="支付宝" className="w-5 h-5 object-contain" />
                <span className="text-sm">支付宝</span>
              </button>
            </div>

            {/* 二维码展示核心区 */}
            <div
              className={`relative p-5 rounded-2xl mb-4 border transition-all duration-300 overflow-hidden ${
                !isAgreed
                  ? 'bg-background-50 border-foreground-100'
                  : 'bg-white border-foreground-200/80 shadow-[0_8px_30px_rgba(0,0,0,0.06)]'
              }`}
            >
              {/* 未同意协议前的遮罩 */}
              {!isAgreed && (
                <div className="absolute inset-0 z-20 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center p-4 text-center rounded-2xl">
                  <div className="text-foreground-800 text-sm font-semibold mb-1">付款前请阅读并同意</div>
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 text-xs mb-4 hover:text-primary-700 underline underline-offset-2 transition-colors"
                  >
                    《社区服务与购买协议》
                  </a>
                  <button
                    onClick={() => setIsAgreed(true)}
                    className="w-4/5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full shadow-[0_4px_16px_rgba(46,175,102,0.3)] hover:shadow-[0_6px_20px_rgba(46,175,102,0.4)] transition-all transform active:scale-95 cursor-pointer"
                  >
                    同意并支付
                  </button>
                </div>
              )}

              {/* 二维码卡片与过期刷新蒙层 */}
              <div
                className={`flex flex-col items-center justify-center transition-all duration-300 ${
                  !isAgreed ? 'blur-sm opacity-20' : 'opacity-100'
                }`}
              >
                {qrCodeLoading ? (
                  <div className="w-[180px] h-[180px] flex items-center justify-center">
                    <div className="w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : isExpired ? (
                  <div className="w-[180px] h-[180px] bg-background-50/90 rounded-xl flex flex-col items-center justify-center text-center p-3 border border-foreground-100">
                    <span className="text-foreground-500 text-xs mb-2.5 font-medium">二维码已过期</span>
                    <button
                      onClick={handleRefresh}
                      className="px-3.5 py-1.5 bg-primary-50 text-primary-600 hover:bg-primary-100 rounded-full text-xs font-semibold border border-primary-200 transition-colors cursor-pointer"
                    >
                      点击刷新
                    </button>
                  </div>
                ) : (
                  <QRCodeCanvas
                    value={paymentData.payUrl || 'https://example.com'}
                    size={180}
                    logoType={payType === 'WECHAT' ? 'wechat' : 'alipay'}
                  />
                )}
              </div>
            </div>

            {/* 倒计时文本提示 */}
            {isAgreed && !isExpired && (
              <div className="text-xs text-foreground-400 mb-4 flex items-center gap-1 font-mono">
                <span>二维码有效期:</span>
                <span className="text-primary-600 font-semibold">{countdownText}</span>
              </div>
            )}
          </div>

          {/* 底部协议提示 */}
          <div className="mb-6 text-[12px] text-foreground-400 text-center">
            {isAgreed && <span className="mr-1">支付即代表您已同意</span>}
            <a
              href={termsUrl}
              target="_blank"
              rel="noreferrer"
              className="text-foreground-500 hover:text-primary-600 underline underline-offset-2 transition-colors"
            >
              《社区服务与购买协议》
            </a>
          </div>

          {/* 底部按钮栏 */}
          <div className="w-full flex gap-3 pt-4 border-t border-foreground-100/80">
            <button
              onClick={closePayModal}
              className="flex-1 py-2.5 rounded-full border border-foreground-200 text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-foreground-900 transition-all cursor-pointer"
            >
              取消
            </button>
            <button
              onClick={() => checkStatus(true)}
              className="flex-1 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold shadow-md shadow-primary-500/25 hover:bg-primary-600 active:scale-95 transition-all cursor-pointer"
            >
              我已完成支付
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
